import { motion } from "motion/react";
import { bool, func, oneOf, string } from "prop-types";
import { useEffect } from "react";

import styled, { useTheme } from "styled-components/macro";
import { backgroundColor } from "theme";

import { use2FAState, useRequestAuthCode } from "Login/TwoFA/API/queryHooks";
import { useTwoFAContext } from "Login/TwoFA/TwoFA";
import {
  useAccentColor,
  useIsDark,
  useIsLegacy,
  useUsePassword,
} from "Login/TwoFA/stateHooks";
import { AUTH_CODE_LENGTH } from "Login/constants";

import FnrAuthCodeInput from "UI/FnrAuthCodeInput";
import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrFade from "UI/FnrFade";
import FnrResendCode from "UI/FnrResendCode";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrTextButton from "UI/FnrTextButton";
import FnrTypography from "UI/FnrTypography";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import { colorIsLight } from "Utilities/Colors/colorUtils";

import FactorDisplay from "../FactorDisplay";
import PromptTypography from "../PromptTypography";
import ContainedCompactStartOver from "../StartOver/ContainedCompactStartOver";

const SDiv = styled("div")`
  padding: 0 16px;
`;

const ResendAndSkipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
`;

const MainInputContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  ${backgroundColor}
  ${({ $addPadding }) => ($addPadding ? "padding: 1px;" : "padding: 0px;")}
  transition: background-color 300ms, padding 300ms;
  width: fit-content;
`;

const AuthCodeInput = ({
  code = "",
  setCode,
  allowPassword,
  factorDisplay,
  factorType,
  factorIcon,
  ...rest
}) => {
  const { isWideView, skipAuth: skipAuthRaw } = useTwoFAContext();
  const [, setUsePassword] = useUsePassword();
  const [{ isLoading, loginSuccess, firstFactor, firstFactorType }, refetch] = use2FAState();
  const isLegacy = useIsLegacy();
  const { resendCode, resending } = useRequestAuthCode();
  const accentColor = useAccentColor();
  const {
    palette: { grey, taupe },
  } = useTheme();
  const isDark = useIsDark();

  const handleCodeChange = ({ target: { value } }) => {
    setCode(value);
    if (value.length === AUTH_CODE_LENGTH) setTimeout(refetch);
  };

  const handleUsePassword = () => setUsePassword(true);

  // Clear the code from state when unmounted: https://flocknote.monday.com/boards/7001407104/pulses/7037425929
  useEffect(() => () => setCode(""), [setCode]);

  return (
    <FnrVerticalGap {...rest}>
      <FnrHorizontalGap gap={4}>
        <ContainedCompactStartOver />
        <FnrVerticalGap gap={0} flexGrow={1}>
          <FnrHorizontalGap gap={0} fullWidth>
            <PromptTypography>Enter the code sent to:</PromptTypography>
          </FnrHorizontalGap>
          <FactorDisplay type={factorType}>{factorDisplay}</FactorDisplay>
        </FnrVerticalGap>
      </FnrHorizontalGap>
      <FnrHorizontalGap centered>
        <FnrVerticalGap gap={4}>
          <FnrHorizontalGap centered>
            <MainInputContainer
              $backgroundColor={isWideView ? accentColor : grey}
              $addPadding={isWideView}
            >
              <FnrFade show={isWideView} top={false} left>
                <SDiv>
                  <FnrContainedIcon
                    icon={factorIcon}
                    size="decorative3"
                    color={colorIsLight(accentColor) ? taupe : grey}
                    noPadding
                  />
                </SDiv>
              </FnrFade>
              <FnrAuthCodeInput
                isLoading={isLoading || loginSuccess}
                code={code}
                handleCodeChange={handleCodeChange}
                accentColor={accentColor}
                bgColor="white"
                autoComplete="one-time-code"
                staticBorderColor={isWideView}
                borderRadius="9999px"
              />
            </MainInputContainer>
          </FnrHorizontalGap>
          <FnrVerticalGap gap={4}>
            {allowPassword && (
              <FnrHorizontalGap gap={4} centered wrap="reverse">
                <FnrFade show={allowPassword} top={false} left>
                  <FnrTextButton
                    data-cy="FnrBackButton_Container"
                    icon="fa-asterisk fas"
                    onClick={handleUsePassword}
                    isDark={isDark}
                    isLegacy={isLegacy}
                    subtleHover
                    color="brandFnBlue"
                    fontSize="sm"
                  >
                    Use my password instead
                  </FnrTextButton>
                </FnrFade>
              </FnrHorizontalGap>
            )}
            {skipAuthRaw ? (
              <ResendAndSkipRow>
                <FnrResendCode
                  onClick={resendCode}
                  isLoading={resending}
                  isLegacy={isLegacy}
                  isDark={isDark}
                />
                <FnrTappyShrink>
                  <FnrButtonWrapper
                    onClick={() => skipAuthRaw({ firstFactorType, firstFactor })}
                    data-cy="TwoFA_ContinueAsGuest"
                  >
                    <FnrHorizontalGap gap={4}>
                      <FnrTypography
                        type={isLegacy ? undefined : "copy"}
                        fontSize="sm"
                        color={isDark ? "textLight" : "brandFnBlue"}
                      >
                        Skip
                      </FnrTypography>
                      <FnrContainedIcon
                        size="xs"
                        color="brandFnBlue"
                        icon="fa-angle-double-right fas"
                        noPadding
                      />
                    </FnrHorizontalGap>
                  </FnrButtonWrapper>
                </FnrTappyShrink>
              </ResendAndSkipRow>
            ) : (
              <FnrHorizontalGap centered>
                <FnrResendCode
                  onClick={resendCode}
                  isLoading={resending}
                  isLegacy={isLegacy}
                  isDark={isDark}
                  addHover={allowPassword}
                />
              </FnrHorizontalGap>
            )}
          </FnrVerticalGap>
        </FnrVerticalGap>
      </FnrHorizontalGap>
    </FnrVerticalGap>
  );
};

AuthCodeInput.propTypes = {
  code: string,
  setCode: func,
  allowPassword: bool,
  factorDisplay: string,
  factorType: oneOf(["email", "phone"]),
  factorIcon: oneOf([
    "fa-mobile-alt fas",
    "fa-envelope far",
    "fa-lock-alt fas",
  ]),
};

export default AuthCodeInput;
